import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Rating } from '@uhg-abyss/web/ui/Rating';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { notXlScreenOnly, tabletScreen } from '../../../common/ConstantsStyles';
import { StyledNullAttributeDiv } from '../../../frontends/ProviderSearch/routes/MapView/CompareDrawer/utility/compareDrawer.styled';
import { NULL_ATTRIBUTE_STATE } from '../../../frontends/ProviderSearch/routes/MapView/CompareDrawer/utility/compareDrawerConstants';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { returnSuppressFlag } from '../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import {
  FlexRatingContainer,
  NoRatingText,
  ProviderCardContent,
  ProviderReviewDot,
  RatingText,
  RatingTextWrapper,
  RatingsReviewText,
  RatingsText,
  UnavailableTextListView,
  UnderlineRatingText,
  VoiceOnlyText,
  VoiceOnlyTextSpan,
} from '../DataCard.style';
import {
  groupedPinRatingStyles,
  ratingFontSizeStyles,
} from './ProviderRating.styles';
import { StarRating } from './StarRating';

const ratingTextCss = (isGroupedLocationDrawer, enableMapViewHideOption) =>
  isGroupedLocationDrawer
    ? {
        '.abyss-text-root': {
          fontSize: '14px',
          fontWeight: '400',
        },
      }
    : enableMapViewHideOption
    ? {
        '.abyss-text-root': {
          fontSize: '16px',
          fontWeight: 'normal',
          '@media (max-width: 744px)': {
            fontSize: '14px',
          },
        },
      }
    : {};

type Props = {
  healthGradeRating: string | undefined;
  providerId: string;
  sectionTypeDerived: string;
  popupContainer?: boolean;
  surveyCount?: number;
  'data-testid'?: string;
  hideNumber?: boolean;
  underline?: boolean;
  enableMapViewHideOption?: boolean;
  expandedProviderListView?: boolean;
  isGroupedLocationDrawer?: boolean;
  providerRatingFocusRef?: React.RefObject<HTMLDivElement>;
};
export const ProviderRating = ({
  popupContainer,
  healthGradeRating,
  providerId,
  sectionTypeDerived,
  'data-testid': testId = '',
  surveyCount,
  hideNumber,
  underline,
  enableMapViewHideOption = false,
  expandedProviderListView,
  isGroupedLocationDrawer,
  providerRatingFocusRef,
}: Props) => {
  const suppressRatings = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const [providerRatings] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.GET_PROVIDER_RATINGS,
  ]);
  const surveyRating = surveyCount ? ` (${surveyCount})` : '';
  const surveyRatingWithoutBraces = surveyCount ? ` ${surveyCount}` : '';
  const TabletScreen = useMediaQuery(tabletScreen);
  const notXlScreen = useMediaQuery(notXlScreenOnly);
  const isSmallScreen = useMediaQuery('(max-width: 400px)');
  const { t } = useTranslation();

  const isGEHAV2 = shouldUseGehaV2Styling();
  const ratingsRef = useRef<HTMLDivElement>(null);

  // accessibility fix to remove redundant span added by abyss rating component
  useEffect(() => {
    if (ratingsRef.current) {
      const ratingRoot = ratingsRef.current.querySelector('.abyss-rating-root');
      const firstSpan = ratingRoot?.querySelector('span:first-child');
      firstSpan?.remove();
    }
  }, [ratingsRef]);

  return (
    <ProviderCardContent
      as={'a'}
      cssProps={{ enableMapViewHideOption }}
      data-testid="provider-ratings"
      ref={providerRatingFocusRef}
      role="link"
      tabIndex={0}
    >
      {healthGradeRating && !suppressRatings && providerRatings ? (
        <RatingTextWrapper
          css={ratingTextCss(isGroupedLocationDrawer, enableMapViewHideOption)}
          data-auto-testid="rating-wrap"
          data-testid="rating-wrap"
          ref={ratingsRef}
        >
          {enableMapViewHideOption ? (
            <>
              <FlexRatingContainer alignItems="center" direction="row">
                <Rating
                  css={{
                    ...(isGroupedLocationDrawer
                      ? groupedPinRatingStyles(notXlScreen)
                      : ratingFontSizeStyles(
                          expandedProviderListView,
                          TabletScreen,
                          isGEHAV2
                        )),
                    ...(isSmallScreen && {
                      'abyss-rating-star': {
                        width: '16px',
                        '.abyss-icon-symbol': {
                          fontSize: '16px !important',
                        },
                      },
                    }),
                  }}
                  hideRating
                  hideReviews
                  rating={Number(healthGradeRating) ?? 1}
                  reviewCount={surveyCount}
                  reviewDescription={`${surveyCount} ${t('DATA_CARD.REVIEWS')}`}
                  size="$sm"
                />
                <RatingsReviewText>{`${healthGradeRating}`}</RatingsReviewText>
                <ProviderReviewDot aria-hidden="true">.</ProviderReviewDot>
                <VoiceOnlyTextSpan>
                  {t('DATA_CARD.OF_5_STARS')}
                </VoiceOnlyTextSpan>
                <RatingsReviewText>{`${surveyRatingWithoutBraces} ${t(
                  'DATA_CARD.REVIEWS'
                )}`}</RatingsReviewText>
              </FlexRatingContainer>
            </>
          ) : (
            <>
              <VoiceOnlyTextSpan>{`${healthGradeRating} stars ${surveyCount} reviews`}</VoiceOnlyTextSpan>
              <StarRating
                healthGradeRating={healthGradeRating}
                key={`healthgrade-rating-${healthGradeRating}`}
                param={0}
                providerId={providerId}
                ratingIndex={0}
                sectionTypeDerived={sectionTypeDerived}
              />
              {underline ? (
                <UnderlineRatingText
                  aria-hidden="true"
                  className="text"
                  color="$core.color.brand.100"
                  data-auto-testid={testId}
                  data-testid={testId}
                  fontSize={'sm'}
                >
                  {`${healthGradeRating}${surveyRating}`}
                </UnderlineRatingText>
              ) : (
                <RatingText
                  aria-hidden="true"
                  className="text"
                  color={
                    popupContainer
                      ? '$core.color.brand.60'
                      : '$core.color.brand.100'
                  }
                  data-auto-testid={testId}
                  data-testid={testId}
                  fontSize={'sm'}
                  fontWeight={popupContainer ? 'bold' : 'regular'}
                >
                  {`${healthGradeRating}${surveyRating}`}
                </RatingText>
              )}
            </>
          )}
        </RatingTextWrapper>
      ) : (
        healthGradeRating && (
          <RatingTextWrapper
            data-auto-testid="rating-wrap"
            data-testid="rating-wrap"
          >
            {enableMapViewHideOption ? (
              <>
                <FlexRatingContainer alignItems="center" direction="row">
                  <Rating
                    hideRating
                    hideReviews
                    rating={Number(healthGradeRating) ?? 1}
                    reviewCount={surveyCount}
                    reviewDescription={`${surveyCount} ${t(
                      'DATA_CARD.REVIEWS'
                    )}`}
                    size="sm"
                  />
                  <span>{`${healthGradeRating}`}</span>
                  <ProviderReviewDot>.</ProviderReviewDot>
                  <span>{`${surveyRatingWithoutBraces} ${t(
                    'DATA_CARD.REVIEWS'
                  )}`}</span>
                </FlexRatingContainer>
                <VoiceOnlyText>{`${healthGradeRating} stars`}</VoiceOnlyText>
              </>
            ) : (
              <>
                {Constants.RATE_LIMITING.map((param, ratingIndex) => (
                  <StarRating
                    healthGradeRating={healthGradeRating}
                    key={`healthgrade-rating-${healthGradeRating}`}
                    param={param}
                    providerId={providerId}
                    ratingIndex={ratingIndex}
                    sectionTypeDerived={sectionTypeDerived}
                  />
                ))}
                <VoiceOnlyText>{`${healthGradeRating} stars`}</VoiceOnlyText>

                <RatingsText
                  aria-hidden="true"
                  className="text"
                  color="$core.color.brand.100"
                  data-auto-testid={testId}
                  data-testid={testId}
                  fontSize={'sm'}
                >
                  <>{healthGradeRating}</>
                </RatingsText>
              </>
            )}
          </RatingTextWrapper>
        )
      )}
      {!healthGradeRating && !hideNumber ? (
        enableMapViewHideOption ? (
          <UnavailableTextListView
            cssProps={{
              expandedProviderListView,
              isGroupedLocationDrawer,
              TabletScreen,
              isGEHAV2,
            }}
            data-testid="provider-no-rating-list-view"
          >
            {t('DATA_CARD.RATINGS_UNAVAILABLE')}
          </UnavailableTextListView>
        ) : (
          <NoRatingText
            cssProps={{ isGEHAV2 }}
            data-testid="provider-no-rating"
          >
            {t('DATA_CARD.RATINGS_NOT_AVAILABLE')}
          </NoRatingText>
        )
      ) : (
        !healthGradeRating && (
          <StyledNullAttributeDiv>
            {t(NULL_ATTRIBUTE_STATE.HEALTH_GRADE_RATINGS)}
          </StyledNullAttributeDiv>
        )
      )}
    </ProviderCardContent>
  );
};
